import { useEffect } from "react";

const set = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(attrs.tag || "meta");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => { if (k !== "tag" && v != null) el.setAttribute(k, v); });
  return el;
};

/** Sets the title, description, canonical, OG/Twitter tags and JSON-LD for a public page. */
export function useSeo({ title, description, path, image, jsonLd }) {
  useEffect(() => {
    const origin = window.location.origin;
    const url = `${origin}${path || window.location.pathname}`;
    if (title) document.title = title;
    if (description) {
      set('meta[name="description"]', { name: "description", content: description });
      set('meta[property="og:description"]', { property: "og:description", content: description });
      set('meta[name="twitter:description"]', { name: "twitter:description", content: description });
    }
    set('link[rel="canonical"]', { tag: "link", rel: "canonical", href: url });
    set('meta[property="og:title"]', { property: "og:title", content: title });
    set('meta[property="og:url"]', { property: "og:url", content: url });
    set('meta[property="og:type"]', { property: "og:type", content: "website" });
    set('meta[name="twitter:title"]', { name: "twitter:title", content: title });
    set('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    if (image) {
      set('meta[property="og:image"]', { property: "og:image", content: image });
      set('meta[name="twitter:image"]', { name: "twitter:image", content: image });
    }

    let script = document.getElementById("page-jsonld");
    if (jsonLd) {
      if (!script) {
        script = document.createElement("script");
        script.id = "page-jsonld";
        script.type = "application/ld+json";
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(jsonLd);
    } else if (script) {
      script.remove();
    }
    return () => {
      const s = document.getElementById("page-jsonld");
      if (s) s.remove();
    };
  }, [title, description, path, image, jsonLd]);
}

export const breadcrumbs = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map(([name, url], i) => ({
    "@type": "ListItem", position: i + 1, name,
    item: `${window.location.origin}${url}`,
  })),
});

const CURRENCY = { "£": "GBP", "$": "USD", "₹": "INR", "€": "EUR", "A$": "AUD", "C$": "CAD", "AED": "AED" };

/** Everything on a board as an ItemList of Products with Offers, so search engines can show
 *  the price and the picture. `items` are [{name, description, image, price, symbol, unit}]. */
export const offerList = (name, path, items = []) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  name,
  url: `${window.location.origin}${path}`,
  numberOfItems: items.length,
  itemListElement: items.filter((i) => i?.name).map((i, idx) => ({
    "@type": "ListItem",
    position: idx + 1,
    item: {
      "@type": "Product",
      name: i.name,
      ...(i.description ? { description: i.description } : {}),
      ...(i.image ? { image: i.image } : {}),
      offers: {
        "@type": "Offer",
        price: Number(i.price || 0).toFixed(2),
        priceCurrency: CURRENCY[i.symbol] || "USD",
        availability: "https://schema.org/InStock",
        ...(i.unit ? { description: i.unit } : {}),
        url: `${window.location.origin}${path}`,
      },
    },
  })),
});

/** A trading place people search for locally — "farmers market near me". */
export const localBusiness = ({ name, description, path, image, type = "LocalBusiness", area }) => ({
  "@context": "https://schema.org",
  "@type": type,
  name,
  description,
  url: `${window.location.origin}${path}`,
  ...(image ? { image } : {}),
  ...(area ? { areaServed: area } : {}),
});

/** Pick-your-own days and market days read as events, which earn a date carousel.
 *  Returns bare nodes so they can be spread straight into a parent @graph. */
export const eventList = (events = []) =>
  events.filter((e) => e?.name).map((e) => ({
    "@type": "Event",
    name: e.name,
    ...(e.description ? { description: e.description } : {}),
    ...(e.image ? { image: e.image } : {}),
    ...(e.start ? { startDate: e.start } : {}),
    ...(e.end ? { endDate: e.end } : {}),
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    eventStatus: "https://schema.org/EventScheduled",
    location: { "@type": "Place", name: e.place || "The farm", ...(e.where ? { address: e.where } : {}) },
    ...(e.price != null ? {
      offers: {
        "@type": "Offer", price: Number(e.price).toFixed(2),
        priceCurrency: CURRENCY[e.symbol] || "USD",
        availability: "https://schema.org/InStock",
        url: `${window.location.origin}/farmers-market`,
      },
    } : {}),
  }));
